import React, {useState} from "react";
import {observer} from "mobx-react-lite";
import classNames from "classnames";
import intl from "react-intl-universal";

import type {AppState} from "@stores/AppState";

import {useAppState} from "@stores/AppStateContext";

import {WorkspaceUser} from "../../../../API";
import {ItemModelType} from "@stores/WorkspaceStore";

import {UserCard} from "../../../components/UserCard"
import {ContextMenu} from '../../../components/ContextMenu';
import {formatDayOnly, formatTimeOnly} from "../../../../utils/date";
import {NotificationCircleIcon} from "../../../components/icons/NotificationCircleIcon";
import {resendNotificationOne, resendNotifications} from "../../../../actions/notification/resend";
import {ArticleIcon} from "../../../components/icons/ArticleIcon";
import {Button} from "../../../components/Button"

import {screenSizeProvider} from "@stores/ScreenSizeProvider";
import {useCrudServices} from "../../../../platform/hooks/useServiceCombinations";

export interface AvatarGroupUsersContextMenuProps {
    users: WorkspaceUser[],
    event?: React.MouseEvent<HTMLDivElement>,
    itemMST: ItemModelType;
}

export const openAvatarGroupUsersContextMenu = (
    appState: AppState,
    params: AvatarGroupUsersContextMenuProps,
    event: React.MouseEvent<HTMLElement>
) => {
    const targetDom = event.currentTarget;
    appState.addAdditionalContextMenu({
        type: 'avatar-group-users',
        id: params.itemMST.id,
        withOverlay: true,
        isDark: false,
        menuPlacement: 'block-end',
        menuAlignment: 'center',
        VERTICAL_SHIFT: 0,
        HORIZONTAL_SHIFT: 0,
        withPointer: false,
        minWidth: 360,
        maxWidth: 360,
        params: {
            ...params,
        },
        getTargetBoundingBox: () => targetDom.getBoundingClientRect(),
    });
};

export const AvatarGroupUsersContextMenu = observer(
    ({
         users,
         itemMST
     }: AvatarGroupUsersContextMenuProps
    ) => {
        const appState = useAppState();
        const userState = appState.userState;
        const crudServices = useCrudServices();
        const selectedNode = appState.rootStore.view.workspace!.selected;
        const [isResendProcessing, setIsResendProcessing] = useState(false);
        const isMobile = screenSizeProvider.view !== 'desktop';
        const hasUnconfirmedUsers = users.some((wsUser: WorkspaceUser) => {
            const user = userState!.usersDS.users.get(wsUser.userId);
            return !itemMST.acknowledgement[user.id];
        });
        const menuItems = users.map((wsUser: WorkspaceUser) => {
            const user = userState!.usersDS.users.get(wsUser.userId);
            const isConfirmUser = itemMST.acknowledgement[user.id]
            const ActionIcon = !isConfirmUser ? NotificationCircleIcon : undefined;
            return {
                className: isMobile ? "AvatarGroupUser__item" : "",
                ActionIcon: ActionIcon,
                action: async () => {
                    await resendNotificationOne(crudServices, userState!, user, itemMST);
                },
                text:
                    <UserCard
                        className={classNames({
                            "AvatarGroupUser__ConfirmUser": itemMST.acknowledgement[user.id]?.date && isMobile,
                            "AvatarGroupUser__UnConfirmUser": !itemMST.acknowledgement[user.id]?.date && isMobile
                        })}
                        isBoldStyle={isConfirmUser}
                        key={user.id}
                        user={user}
                        size={36}
                        isEmail={false}
                        additionalInfo={
                            itemMST.acknowledgement[user.id]?.date
                                ? (
                                    <div className="body-medium">
                                        {formatDayOnly(new Date(itemMST.acknowledgement[user.id].date))} • {formatTimeOnly(new Date(itemMST.acknowledgement[user.id].date))}
                                    </div>
                                )
                                : null
                        }
                    />,
            };
        });
        let contextMenuMobileBlock = {};
        if (isMobile) {
            contextMenuMobileBlock = {
                title:
                    hasUnconfirmedUsers
                        ? intl.get('AvatarGroup.context.menu.title.unconfirmed').d("Users Unconfirmed")
                        : intl.get('AvatarGroup.context.menu.title.confirmed').d("Users Confirmed"),
                subTitle: itemMST.name,
                SubTitleIcon: ArticleIcon,
                button: hasUnconfirmedUsers &&
                    <Button
                        style={"solid"}
                        type={'primary'}
                        text={intl.get('AvatarGroup.context.menu.send.reminder.all').d('Send Reminder to All')}
                        fullWidth={true}
                        loading={isResendProcessing}
                        onClick={() => {
                            if (isResendProcessing) {
                                return;
                            }
                            setIsResendProcessing(true);
                            resendNotifications(crudServices, userState!, itemMST).then(() => {
                                setIsResendProcessing(false);
                            })
                        }}
                    />
            }
        }
        return (
            <ContextMenu
                {...contextMenuMobileBlock}
                maxHeight={500}
                items={menuItems}
                isDark={false}
                handleClose={() => appState.setContextMenu(null)}
            />
        );
    }
);
